import { paths } from "../../../routes/paths";

/**
 * DrillKind (from sp_app_onestop_rows) -> an EXISTING React route.
 *
 * This report deliberately adds no detail screens of its own - the same call
 * StockMovementReportPage.jsx makes, mapping a movementType discriminator onto
 * the five stock detail routes that already exist. Every target here is built
 * through routes/paths.js rather than a hand-written string, so a route rename
 * moves this with it.
 *
 * Returns null when a row genuinely has nowhere to go. Those render as plain
 * text, not a dead link - see the two honest gaps below.
 */
const RESOLVERS = {
  // Keyed by a globally-unique RecordGuid.
  STOCK_OUT: (r) => (r.drillGuid ? paths.stockOutDetail(r.drillGuid) : null),
  STOCK_IN: (r) => (r.drillGuid ? paths.stockInDetail(r.drillGuid) : null),
  STOCK_RETURN: (r) => (r.drillGuid ? paths.stockReturnDetail(r.drillGuid) : null),
  STOCK_ADJUSTMENT: (r) => (r.drillGuid ? paths.stockAdjustmentDetail(r.drillGuid) : null),
  LPO: (r) => (r.drillGuid ? paths.stockLpoDetail(r.drillGuid) : null),
  SALES_ENQUIRY: (r) => (r.drillGuid ? paths.salesEnquiryDetail(r.drillGuid) : null),
  SALES_ORDER: (r) => (r.drillGuid ? paths.salesOrderDetail(r.drillGuid) : null),
  QUOTATION: (r) => (r.drillGuid ? paths.quotationDetail(r.drillGuid) : null),
  TECHNICAL_WORK: (r) => (r.drillGuid ? paths.technicalWorkDetail(r.drillGuid) : null),
  PETTYCASH_VOUCHER: (r) => (r.drillGuid ? paths.pettyCashVoucherDetail(r.drillGuid) : null),
  EMPLOYEE: (r) => (r.drillGuid ? paths.employeeEdit(r.drillGuid) : null),
  EMPLOYEE_REMARKS: () => paths.employeeRemarks,
  SUPERVISOR_AREA: (r) => (r.drillGuid ? paths.guardingSupervisorAreaDetail(r.drillGuid) : null),
  ATTENDANCE_DASHBOARD: () => paths.guardingAttendanceDashboard,
  LEAVE: () => paths.leaveManagement,

  // Composite (branchId, custId) - custId is only branch-local.
  CUSTOMER_GUARDING: (r) =>
    r.drillId != null && r.drillBranchId != null
      ? paths.customerGuarding(r.drillBranchId, r.drillId)
      : null,

  // Int key, globally unique.
  VEHICLE: (r) => (r.drillId != null ? paths.vehicleEdit(r.drillId) : null),

  // Vehicle maintenance requests have no detail ROUTE of their own - the
  // requests live inside the Vehicles master screen - so this lands on the
  // Maintenance Report, which is the one screen that shows a request's own
  // figures and links onward from there.
  VEHICLE_REQUEST: () => paths.vehicleMaintenanceReport,

  // A fuel purchase (Batch187) opens in the Fuel Transactions page, which reads ?open= and shows
  // that purchase's detail - photos and review flags included - straight away.
  FUEL_TRANSACTION: (r) => (r.drillGuid ? `${paths.fuelTransactions}?open=${r.drillGuid}` : null),

  // Collections has no per-receipt detail screen (the module had an API slice
  // and dashboard widgets but no page at all until this report needed one), so
  // this lands on the Collections list narrowed to that receipt's own day and
  // branch, with the row itself highlighted - rather than pretending a receipt
  // detail route exists. See CollectionsListPage.jsx's `highlight` param.
  COLLECTION: (r) => {
    const params = new URLSearchParams();
    if (r.drillGuid) params.set("highlight", r.drillGuid);
    if (r.drillBranchId != null) params.set("branchId", String(r.drillBranchId));
    const day = r.transactionDate ? String(r.transactionDate).slice(0, 10) : null;
    if (day) {
      params.set("dateFrom", day);
      params.set("dateTo", day);
    }
    const qs = params.toString();
    return qs ? `${paths.collections}?${qs}` : paths.collections;
  },

  // AMC has no /amc/:guid route - the contract opens on the AMC screen through
  // a query param (paths.amcRecord), not a route param.
  AMC_CONTRACT: (r) => (r.drillGuid ? paths.amcRecord(r.drillGuid) : null),
  AMC_EQUIPMENT: (r) => (r.drillGuid ? paths.amcRecord(r.drillGuid) : null),
};

/**
 * Drill kinds that land on a LIST rather than the specific record, because no
 * detail screen for that record exists yet. Surfaced in the UI as "opens the
 * list" so nobody expects the row itself to be highlighted.
 */
export const LIST_ONLY_DRILL_KINDS = new Set(["COLLECTION", "VEHICLE_REQUEST"]);

export const drillTarget = (row) => {
  if (!row?.drillKind) return null;
  const resolver = RESOLVERS[row.drillKind];
  if (!resolver) return null;
  try {
    return resolver(row) || null;
  } catch {
    // A missing paths.* builder must never take the whole report down.
    return null;
  }
};

export const isListOnlyDrill = (row) => !!row?.drillKind && LIST_ONLY_DRILL_KINDS.has(row.drillKind);
